import { MapPin } from "lucide-react";
import { useState } from "react";

import { Alert, Badge, Button, Card, Input, Label } from "@realroof/ui";

import { api } from "../api";
import { when } from "../format";

export interface PendingLocalityView {
  id: string;
  name: string;
  landmark: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  suggested_at: string | null;
  similar: { id: string; name: string; distance_km: number | null }[];
}

/**
 * A colony the owner could not find and suggested. Highlighted at the top of the review so the team
 * decides first: add it for everyone, say it is an existing locality, or reject it.
 */
export function PendingLocality({ place, canDecide, onDecided }: { place: PendingLocalityView; canDecide: boolean; onDecided: (note: string) => void }) {
  const [name, setName] = useState(place.name);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function run(path: string, body: object, note: string) {
    setBusy(true);
    setError("");
    try {
      await api(`/admin/locations/${place.id}/${path}`, { method: "POST", body });
      onDecided(note);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card role="region" aria-label="New locality suggested" className="border-2 border-premium bg-premium-soft">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="warning">Decide first</Badge>
        <strong>
          New locality suggested: {place.name}
          {place.city ? `, ${place.city}` : ""}
        </strong>
      </div>
      <p className="text-sm">
        {place.landmark ? `Landmark: ${place.landmark}. ` : ""}
        {place.lat != null && place.lng != null && (
          <a href={`https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1">
            <MapPin aria-hidden="true" className="size-4" /> See the point on a map
          </a>
        )}
        {place.suggested_at ? ` · suggested ${when(place.suggested_at)}` : ""}
      </p>
      {error && <Alert tone="error">{error}</Alert>}
      {canDecide ? (
        <>
          <div className="flex flex-wrap items-end gap-2">
            <Label className="min-w-56 flex-1">
              Name as it will appear (fix the spelling if needed)
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Label>
            <Button variant="primary" disabled={busy || name.trim().length < 2} onClick={() => run("approve", name.trim() !== place.name ? { name: name.trim() } : {}, `${name.trim()} is added; everyone can find it now.`)}>
              Approve new locality
            </Button>
          </div>
          {place.similar.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm">Or is it one of these existing localities? (its name then becomes an alias)</span>
              <div className="flex flex-wrap gap-1.5">
                {place.similar.map((s) => (
                  <Button key={s.id} size="sm" disabled={busy} onClick={() => run("reject", { into_id: s.id }, `Mapped to ${s.name}; the ad now uses ${s.name}.`)}>
                    It is {s.name}
                    {s.distance_km != null ? ` (${s.distance_km} km)` : ""}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <Button variant="link" className="self-start text-destructive" disabled={busy} onClick={() => window.confirm(`Reject "${place.name}"? The ad loses its locality; send it back so the owner picks one.`) && run("reject", {}, `${place.name} rejected. Send the ad back so the owner chooses a locality.`)}>
            Not a real place: reject
          </Button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">A moderator decides on new localities.</p>
      )}
    </Card>
  );
}
